import { UIElement } from '../ui-element';
import {
    isVisible,
    createElement,
    dispatchCustomEvent,
    getDebugMode,
} from '../../global/ui-helpers';
import { browser } from '../../global/browser';
import { eventBus } from '../../global/event-bus';
import { waitForAnimations, isInFixedLayer } from '../../global/helpers';
import { UIModal } from './ui-modal';
import { Component } from '../../decorators/component.decorator';
import type {
    UIModalConfig,
    UIModalUpdateActions,
    UIModalUpdateListDetail,
} from './ui-modal-controller.types';
import type { EventHandler, NullOr } from '../../global/utils.type';
import { focusHandler } from '../../global/focus-handler';
import type { ElementConfig } from '../ui-element.type';
import type { UIModalType } from './ui-modal.types';
import styles from './ui-modal-controller.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIModalController
 * @element ui-modal-controller
 * @classdesc Represents a class for <code>ui-modal-controller</code> element.
 * Technical element to control modals. Open, close, place, etc. modals correctly,
 * based on events.
 * @fires event:modal-controller-ready
 * @fires event:modal-close
 * @listens keydown
 * @listens event:modal-open
 * @listens event:modal-open-enter
 * @listens event:modal-close
 * @listens event:modal-close-enter
 * @listens event:modal-close-top
 * @listens event:modal-updatelist
 * @listens event:popover-updated
 * @example <ui-modal-controller></ui-modal-controller>
 */
@Component({
    tag: 'ui-modal-controller',
    styles,
})
export class UIModalController extends UIElement {
    /**
     * State: Determines the modal in which focus trap is currently enabled
     * @type {UIModal | undefined}
     */
    currentFocusedModal?: UIModal | undefined;

    /**
     * @type {Array<HTMLElement>}
     */
    focusReturnPoints: Array<HTMLElement> = [];

    /**
     * @type {UIModalConfig}
     */
    config: UIModalConfig = {};

    static readonly UpdateActions: UIModalUpdateActions = {
        Add: 'add',
        Remove: 'remove',
    };

    private modalOpenHandler!: EventHandler;
    private modalCloseHandler!: EventHandler;
    private updateListHandler!: EventHandler;
    private topModalCloseHandler!: EventHandler;
    private modalCloseOnEscKeyHandler!: (event: KeyboardEvent) => void;

    /**
     * @type {string}
     */
    static get FullViewPortModalSelector(): string {
        return [UIModal.Type.Popover, UIModal.Type.Bubble, UIModal.Type.Menu]
            .map((type) => `:not([type="${type}"])`)
            .join('');
    }

    /**
     * Gets all currently action modals.
     * @returns {Array<UIModal>}
     */
    getActiveModals(): Array<UIModal> {
        return Array.from(
            document.querySelectorAll<UIModal>(
                'ui-modal.-active:not([type="bubble"])'
            )
        ).filter(isVisible);
    }

    /**
     * Gets all currently active full viewport modals (also popovers[align=fixed] on mobile).
     * @returns {Array<UIModal>}
     */
    getActiveFullViewModals(): Array<UIModal> {
        const isMobile = browser.mobileMediaMatches();
        const isTablet = browser.tabletMediaMatches();
        const selector =
            'ui-modal.-active' +
            UIModalController.FullViewPortModalSelector +
            (isMobile
                ? ', ui-modal.-active[type="popover"][align="fixed"], ui-modal[bottom-sheet]'
                : '') +
            (isTablet
                ? ', ui-modal.-active[type="popover"][align="fixed"][tablet-full]'
                : '');
        return Array.from(document.querySelectorAll<UIModal>(selector)).filter(
            isVisible
        );
    }

    /**
     * Get the top modal window dialog, not a type popover (no tooltip, no dropdown, etc.).
     * @returns {UIModal | null}
     */
    getActiveTopModalWindow(): UIModal | null {
        const selector =
            'ui-modal.-active' + UIModalController.FullViewPortModalSelector;
        const modals = this.sortModalsByOrder(
            Array.from(document.querySelectorAll<UIModal>(selector)).filter(
                isVisible
            )
        );
        if (modals.length > 0) {
            return modals[modals.length - 1];
        }
        return null;
    }

    /**
     * Sort all modals by order
     * @param {Array<UIModal>} modals
     * @param {boolean} [reverse]
     * @returns {Array<UIModal>}
     */
    sortModalsByOrder(
        modals: Array<UIModal>,
        reverse: boolean = false
    ): Array<UIModal> {
        const sortFn = (a: UIModal, b: UIModal): number =>
            a.order > b.order
                ? reverse
                    ? -1
                    : 1
                : a.order === b.order
                  ? 0
                  : -1;
        modals.sort(sortFn);
        return modals;
    }

    /**
     * Shifted modals are moved a bit by side.
     * @private
     * @returns {UIModalController | undefined}
     */
    private shiftModalWindow(): UIModalController | undefined {
        const modals = this.getActiveFullViewModals().filter(
            (modal) => !modal.isPopover()
        );
        const unshiftedModals = this.sortModalsByOrder(modals).filter(
            (modal) => !modal.shifted
        );

        if (unshiftedModals.length < 2) {
            return;
        }

        const modal = unshiftedModals[0];

        if (modal?.isVisible()) {
            modal.shifted = true;
        }
        return this;
    }

    /**
     * Un-shifted modals are placed exactly under each-other.
     * @private
     * @returns {UIModalController}
     */
    private unshiftModalWindow(): UIModalController {
        const modals = this.getActiveFullViewModals().filter(
            (modal) => !modal.isPopover()
        );
        const shiftedModals = this.sortModalsByOrder(modals).filter(
            (modal) => modal.shifted
        );

        const modal = shiftedModals[shiftedModals.length - 1];

        if (modal?.isVisible()) {
            modal.shifted = false;
        }
        return this;
    }

    /**
     * Create a new window by given params.
     * @param {UIModalConfig} config
     * @returns {UIModal}
     */
    createNewModal(config: UIModalConfig): UIModal {
        const params = config.params || {};
        const attributes: Record<string, unknown> = params.attributes || {};
        attributes.type = config.type;
        attributes.dynamic = true;
        // TODO: Remove it later, use general solution for attributes
        if (params.hasOwnProperty('id')) {
            attributes.id = params.id;
        }

        let isFixed = false;
        if (config.params?.target) {
            isFixed = isInFixedLayer(config.params?.target);
        }
        const element: ElementConfig = {
            tagName: 'ui-modal',
            attributes: attributes,
            classList: {
                '-position-fixed': isFixed,
            },
            events: {},
            children: [],
        };

        let onRemove: (() => void) | undefined;

        if (
            config.content instanceof HTMLElement ||
            config.content instanceof DocumentFragment
        ) {
            (element.children as Array<HTMLElement | DocumentFragment>).push(
                config.content
            );
            (element.attributes as Record<string, unknown>).bubble =
                config.type === UIModal.Type.Bubble;
            const target = config.content;
            const nextPosition = target.nextSibling;
            const parent = target.parentElement;
            const isPopoverType = (
                [
                    UIModal.Type.Popover,
                    UIModal.Type.Bubble,
                    UIModal.Type.Menu,
                ] as Array<UIModalType>
            ).includes(config.type!);
            if (parent && isPopoverType) {
                onRemove = () => {
                    nextPosition?.parentNode === parent
                        ? parent.insertBefore(target, nextPosition)
                        : parent.appendChild(target);
                };
            }
        } else if (config.content) {
            const contentData = config.content;
            (element.children as Array<ElementConfig>).push(
                {
                    tagName: 'h2',
                    children: [contentData.title],
                },
                {
                    tagName: 'p',
                    children: [contentData.description],
                }
            );
        }

        if (
            config.footer instanceof HTMLElement ||
            config.footer instanceof DocumentFragment
        ) {
            const footerEl = config.footer;
            const footerNextSibling = footerEl.nextSibling;
            const footerParent = footerEl.parentElement;
            let footerWrapper: HTMLElement;
            if (
                footerEl instanceof HTMLElement &&
                footerEl.localName === 'footer'
            ) {
                footerWrapper = footerEl;
            } else {
                footerWrapper = document.createElement('footer');
                footerWrapper.appendChild(footerEl);
            }
            (element.children as Array<HTMLElement | DocumentFragment>).push(
                footerWrapper
            );
            const prevOnRemove = onRemove;
            onRemove = () => {
                if (footerParent) {
                    footerNextSibling?.parentNode === footerParent
                        ? footerParent.insertBefore(footerEl, footerNextSibling)
                        : footerParent.appendChild(footerEl);
                }
                prevOnRemove?.();
            };
        }

        if (onRemove) {
            element.events!['modal-updatelist'] = (
                event?: Event | CustomEvent<UIModalUpdateListDetail>
            ) => {
                if (
                    (event as CustomEvent<UIModalUpdateListDetail>)?.detail
                        .action === UIModalController.UpdateActions.Remove
                ) {
                    onRemove();
                }
            };
        }

        if (config.params) {
            // TODO: remove it later, use
            if (config.params.hasOwnProperty('compact')) {
                console.warn(
                    'The property config.params.compact is deprecated.' +
                        ' Use config.params.attributes for additional attributes instead'
                );
                (element.attributes as Record<string, unknown>).compact =
                    String(config.params.compact);
            }

            if (config.params.hasOwnProperty('closable')) {
                console.warn(
                    'The property config.params.closable is deprecated.' +
                        ' Use config.params.attributes for additional attributes instead'
                );
                (element.attributes as Record<string, unknown>).closable =
                    String(config.params.closable);
            }

            if (config.params.hasOwnProperty('noclosable')) {
                console.warn(
                    'The property config.params.noclosable is deprecated.' +
                        ' Use config.params.attributes for additional attributes instead'
                );
                (element.attributes as Record<string, unknown>).noclosable =
                    String(config.params.noclosable);
            }

            if (config.params.hasOwnProperty('layout')) {
                console.warn(
                    'The property config.params.layout is deprecated.' +
                        ' Use config.params.attributes for additional attributes instead'
                );
                (element.attributes as Record<string, unknown>).layout = String(
                    config.params.layout
                );
            }
        }

        const modal: UIModal = createElement(element)!;
        document.body.appendChild(modal);

        if (config.params && typeof config.params.position === 'function') {
            const handleViewportChanges = () => {
                const pos = config.params!.position!();
                modal.style.left = pos.x ?? '';
                // CSS custom property mirror of style.left — used in ui-modal.css to
                // calculate max-width: calc(100vw - var(--ui-modal-left)) so the popover
                // never overflows the right edge of the viewport.
                modal.style.setProperty('--ui-modal-left', pos.x ?? '0px');
                modal.style.top = pos.y ?? '';
                modal.setAttribute('align', pos.align ?? '');
                modal.setAttribute('shift', pos.shift ?? '');
                modal.setAttribute('adjusted', 'adjusted');
                const activeModals = this.getActiveFullViewModals();
                const shouldBlockBodyScroll =
                    (modal.bottomSheet && browser.mobileMediaMatches()) ||
                    activeModals.length > 0;
                this.toggleBodyScroll(shouldBlockBodyScroll);
            };
            let closestModal: NullOr<UIModal>;
            const lockId = Date.now();

            if (config.params.target) {
                closestModal = config.params.target.closest('ui-modal');
                if (closestModal) {
                    closestModal.locked = true;
                    closestModal.lockedBy = lockId;
                }
            }

            window.addEventListener('resize', handleViewportChanges);
            eventBus.addEventListener('popover-updated', handleViewportChanges);
            if (modal.isAnimated()) {
                modal.addEventListener(
                    'modal-open-enter',
                    handleViewportChanges
                );
            }

            const handleModalUpdateList = (
                event: CustomEvent<UIModalUpdateListDetail>
            ) => {
                if (
                    event.detail.action === UIModalController.UpdateActions.Add
                ) {
                    handleViewportChanges();
                }
                if (
                    event.detail.action ===
                    UIModalController.UpdateActions.Remove
                ) {
                    window.removeEventListener('resize', handleViewportChanges);
                    eventBus.removeEventListener(
                        'popover-updated',
                        handleViewportChanges
                    );
                    if (modal.isAnimated()) {
                        modal.removeEventListener(
                            'modal-open-enter',
                            handleViewportChanges
                        );
                    }
                    if (closestModal?.lockedBy === lockId) {
                        closestModal.locked = false;
                        closestModal.lockedBy = null;
                    }
                }
            };

            modal.addEventListener(
                'modal-updatelist',
                handleModalUpdateList as EventListener
            );
        }
        return modal;
    }

    /**
     * If modal is opened and this method receives 'true' argument it prevents body
     * or other modal to scroll. Otherwise body and other modals scrolling will be
     * enabled.
     * @param {boolean} lock
     */
    toggleBodyScroll(lock: boolean): void {
        const doc = document.documentElement;
        if (doc.classList.contains('-no-scroll') === lock) {
            /* Is already in correct state */
            return;
        }

        if (lock) {
            const scrollBarWidth = window.innerWidth - doc.clientWidth;
            if (scrollBarWidth > 0) {
                doc.classList.add('-preserve-scroll');
                const scrollTop = doc.scrollTop
                    ? doc.scrollTop
                    : document.body.scrollTop;
                if (scrollTop > 0) {
                    doc.style.top = -1 * scrollTop + 'px';
                }
            }
            doc.classList.add('-no-scroll');
        } else {
            doc.classList.remove('-no-scroll');
            doc.classList.remove('-preserve-scroll');
            const scrollValue = Math.abs(parseInt(doc.style.top));
            if (scrollValue) {
                doc.style.top = '';
                doc.scrollTop = scrollValue;
                if (!doc.scrollTop) {
                    document.body.scrollTop = scrollValue;
                }
            }
        }
    }

    /**
     * Modal open callback.
     * @param {CustomEvent<UIModalConfig>} e
     * @private
     */
    private handleModalOpen(e: CustomEvent<UIModalConfig>): void {
        const config: UIModalConfig = e.detail || {};
        this.config = config;

        let modal: UIModal | null = null;

        if (!config.selector && !config.content) {
            return;
        }

        if (config.selector) {
            modal = document.querySelector<UIModal>(config.selector);
            if (modal?.localName !== 'ui-modal' || modal.isActive()) {
                return;
            }
        }

        if (config.content) {
            modal = this.createNewModal(config);
        }

        if (!modal) {
            return;
        }

        const hasActionHandler =
            !!config.params && typeof config.params.onAction === 'function';
        const hasCloseHandler =
            !!config.params && typeof config.params.onClose === 'function';
        const hasOpenHandler =
            !!config.params && typeof config.params.onOpen === 'function';
        const classList = config.params ? config.params.classList || {} : {};
        const closestModal = (e.target as HTMLElement).closest<UIModal>(
            'ui-modal'
        );
        const modalParent = modal.parentElement;
        const modalNextSibling = modal.nextSibling;
        if (closestModal) {
            classList['-sub-modal'] = true;
        }
        if (modal.detached) {
            document.body.appendChild(modal);
        }

        const modals = this.sortModalsByOrder(this.getActiveModals());
        const order =
            (modals.length ? modals[modals.length - 1].order : modals.length) +
            1;

        const handleModalUpdateList = (
            event?: Event | CustomEvent<UIModalUpdateListDetail>
        ) => {
            if (!event) {
                return;
            }
            const customEvent = event as CustomEvent<UIModalUpdateListDetail>;
            if (
                customEvent.detail.action ===
                UIModalController.UpdateActions.Add
            ) {
                if (hasOpenHandler) {
                    config.params!.onOpen!();
                }
                return;
            }
            if (
                customEvent.detail.action ===
                UIModalController.UpdateActions.Remove
            ) {
                if (modal.detached && modalParent) {
                    if (modalNextSibling) {
                        modalParent.insertBefore(modal, modalNextSibling);
                    } else {
                        modalParent.appendChild(modal);
                    }
                }

                modal.removeEventListener(
                    'modal-updatelist',
                    handleModalUpdateList
                );
                this.handleModalFocus();
            }
        };

        modal.updateElement({
            attributes: { order: order },
            classList: classList,
            events: {
                ...(hasActionHandler && {
                    'modal-action': config.params!.onAction,
                }),
                ...(hasCloseHandler && {
                    'modal-close': config.params!.onClose,
                }),
                'modal-updatelist': handleModalUpdateList,
            },
        });

        modal.open();
        this.handleModalFocus();

        // Do not save return point of the invoker (like button) in following cases:
        const hasReturnPoint = ![
            '-dropdown',
            '-autocomplete',
            '-messagebox',
            '-menu',
        ].some((s) => modal.classList.contains(s));
        if (hasReturnPoint) {
            this.focusReturnPoints.push(document.activeElement as HTMLElement);
            waitForAnimations(modal.modalWindow)
                .then(() => modal.focus())
                .catch((e) => {
                    if (getDebugMode()) {
                        console.warn(
                            `ui-modal: closed before focus could be set`,
                            this,
                            e
                        );
                    }
                });
        }
    }

    /**
     * Modal close callback.
     * @param {CustomEvent<UIModalConfig>} e
     * @private
     */
    private handleModalClose(e: CustomEvent<UIModalConfig>): void {
        const modal = (e.target as HTMLElement).closest<UIModal>('ui-modal');
        if (
            modal &&
            !([UIModal.Type.Popover] as Array<UIModalType>).includes(
                modal.type!
            )
        ) {
            modal.close();
            this.restoreFocusOnModalClose(modal);
        }
        this.handleModalFocus();
    }

    /**
     * Closes top active modal window.
     * @param {CustomEvent<UIModalConfig>} [e]
     */
    private handleTopModalClose(e?: CustomEvent<UIModalConfig>): void {
        const topModal = this.getActiveTopModalWindow();

        if (topModal) {
            topModal.close();
            this.restoreFocusOnModalClose(topModal);
        }

        this.handleModalFocus();
    }

    /**
     * Restores focus to a return point when a modal is closed.
     * @param {UIModal} modal
     * @private
     */
    private restoreFocusOnModalClose(modal: UIModal): void {
        const hasReturnPoint = this.focusReturnPoints?.length > 0;
        const isMatchingType =
            modal &&
            !([UIModal.Type.Popover] as Array<UIModalType>).includes(
                modal.type!
            );

        if (isMatchingType && hasReturnPoint) {
            const target = this.focusReturnPoints.pop();
            target?.focus();
        }
    }

    /**
     * If current top modal exists but is not focused,
     * remove previous focus and set focus on current modal.
     *
     * If no modal just remove focus.
     * @private
     */
    private handleModalFocus(): void {
        if (this.config.params?.disableKeyboardTrapping) {
            return;
        }

        const currentModal = this.getActiveTopModal();
        const currentFocusedModal = this.currentFocusedModal;

        // Modal doesn't exist anymore but focus data is still there
        if (!currentModal && currentFocusedModal) {
            // Remove focus
            focusHandler.destroyFocusTrap(
                currentFocusedModal,
                this.modalCloseOnEscKeyHandler
            );
            this.currentFocusedModal = undefined;
            return;
        }

        // Modal exists but is not focused
        if (currentModal && currentModal !== currentFocusedModal) {
            // Remove previous focus
            focusHandler.destroyFocusTrap(
                currentFocusedModal!,
                this.modalCloseOnEscKeyHandler
            );
            // Set focus on current modal
            focusHandler.createFocusTrap(
                currentModal,
                this.modalCloseOnEscKeyHandler
            );
            this.currentFocusedModal = currentModal;
        }
    }

    /**
     * Modal update list callback.
     * @param {CustomEvent<UIModalUpdateListDetail>} e
     * @private
     */
    private handleUpdateList(e: CustomEvent<UIModalUpdateListDetail>): void {
        const modal = e.target as UIModal;
        const order = modal.getAttribute('order');
        const isPopover =
            (typeof modal.isPopover === 'function' && modal.isPopover()) ||
            false;

        const shouldDisabledBodyScroll =
            !isPopover ||
            modal.align === UIModal.Alignments.Fixed ||
            (modal.type === UIModal.Type.Menu && !modal.noBottomSheet);
        const shouldShiftModal = !isPopover && order && Number(order) > 1;

        if (shouldDisabledBodyScroll) {
            const activeModals = this.getActiveFullViewModals();
            this.toggleBodyScroll(activeModals.length > 0);
        }

        if (shouldShiftModal) {
            switch (e.detail.action) {
                case UIModalController.UpdateActions.Add:
                    this.shiftModalWindow();
                    break;
                case UIModalController.UpdateActions.Remove:
                    this.unshiftModalWindow();
                    break;
            }
        }
    }

    /**
     * Get active top modal element.
     * @returns {UIModal | void}
     * @private
     */
    private getActiveTopModal(): UIModal | void {
        const modals = Array.from(
            document.querySelectorAll<UIModal>('ui-modal.-active')
        ).filter(isVisible);
        if (!modals.length) {
            return;
        }

        return modals.pop();
    }

    /**
     * Exit from top modal via keyboard ESC key.
     * Arrow function to bind 'this' for sorting modals.
     * @param {KeyboardEvent | CustomEvent<UIModalConfig>} event
     * @private
     * @fires event:modal-close
     */
    private handleModalCloseOnEscKey = (
        event: KeyboardEvent | CustomEvent<UIModalConfig>
    ): void => {
        const activeModals = document.querySelectorAll<UIModal>(
            'ui-modal.-active:not(.-annotation)'
        );
        const modals = Array.from(activeModals).filter(isVisible);
        if (!modals.length) {
            return;
        }
        const topModal = this.sortModalsByOrder(modals).pop();

        // Prevent closing of non-closable modals
        if (!topModal?.isClosable()) {
            return;
        }

        // Close modal
        event.preventDefault();
        dispatchCustomEvent(topModal, 'modal-close', {});
    };

    /**
     * @fires event:modal-controller-ready
     * @inheritDoc
     */
    hydrate(): void {
        this.modalOpenHandler = this.handleModalOpen.bind(this) as EventHandler;
        this.modalCloseHandler = this.handleModalClose.bind(
            this
        ) as EventHandler;
        this.updateListHandler = this.handleUpdateList.bind(
            this
        ) as EventHandler;
        this.topModalCloseHandler = this.handleTopModalClose.bind(
            this
        ) as EventHandler;
        this.modalCloseOnEscKeyHandler =
            this.handleModalCloseOnEscKey.bind(this);

        eventBus.addEventListener('modal-open', this.modalOpenHandler);
        eventBus.addEventListener('modal-close', this.modalCloseHandler);
        eventBus.addEventListener('modal-close-top', this.topModalCloseHandler);
        eventBus.addEventListener('modal-updatelist', this.updateListHandler);

        this.focusReturnPoints = [];

        this.dispatchCustomEvent('modal-controller-ready', {});
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.handleModalFocus();
        eventBus.removeEventListener('modal-open', this.modalOpenHandler);
        eventBus.removeEventListener('modal-close', this.modalCloseHandler);
        eventBus.removeEventListener(
            'modal-close-top',
            this.topModalCloseHandler
        );
        eventBus.removeEventListener(
            'modal-updatelist',
            this.updateListHandler
        );
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        eventBus.addEventListener('modal-open', this.modalOpenHandler);
        eventBus.addEventListener('modal-close', this.modalCloseHandler);
        eventBus.addEventListener('modal-close-top', this.topModalCloseHandler);
        eventBus.addEventListener('modal-updatelist', this.updateListHandler);
    }
}
